/** Per-device reading preferences (text size, paper theme, module reading direction). */
import { useCallback, useState } from "react";

export type PaperTheme = "light" | "dark" | "app";
export type ReaderDirection = "horizontal" | "vertical";

const KEY = "soorgaa_reader_prefs";

type Prefs = { size: 0 | 1 | 2; paper: PaperTheme; direction: ReaderDirection; dataSaver: boolean };
const DEFAULTS: Prefs = { size: 1, paper: "app", direction: "horizontal", dataSaver: false };

function read(): Prefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULTS;
    const p = JSON.parse(raw) as Partial<Prefs>;
    return {
      size: p.size === 0 || p.size === 2 ? p.size : 1,
      paper: p.paper === "light" || p.paper === "dark" ? p.paper : "app",
      direction: p.direction === "vertical" ? "vertical" : "horizontal",
      dataSaver: p.dataSaver === true,
    };
  } catch {
    return DEFAULTS;
  }
}

export function getReaderDirection(): ReaderDirection {
  return read().direction;
}

/** Data saver: no automatic image loading and no page prefetch in the readers. */
export function getDataSaver(): boolean {
  return read().dataSaver;
}

export function setDataSaver(dataSaver: boolean): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...read(), dataSaver }));
  } catch {
    /* preference not saved */
  }
}

export function setReaderDirection(direction: ReaderDirection): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...read(), direction }));
  } catch {
    /* preference not saved */
  }
}

export function useReaderPrefs() {
  const [prefs, setPrefs] = useState<Prefs>(read);
  const update = useCallback((patch: Partial<Prefs>) => {
    setPrefs((cur) => {
      const next = { ...cur, ...patch };
      try {
        localStorage.setItem(KEY, JSON.stringify(next));
      } catch {
        /* preference not saved */
      }
      return next;
    });
  }, []);
  return { prefs, update };
}
